/**
 * Impresión vía servidor (relay).
 *
 * El navegador NO habla con el Mac: deja el trabajo en el servidor (POST /api/print/jobs) y el
 * agente que corre en el Mac lo recoge y lo manda a la Zebra. Así funciona igual en Safari, Chrome
 * o Firefox (ya no se llama a http://localhost, que esos navegadores bloquean).
 *
 * Mantiene la interfaz de la versión anterior (getAgentConfig / setAgentConfig / pingAgent /
 * printZplDirect), así que los modales de impresión casi no cambian.
 */
import { api } from "@/lib/api";

const LS_KEY = "zebralab_agent_config";
const FINAL_STATES = new Set(["done", "error", "expired", "cancelled"]);
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** Preferencias de este navegador: impresora elegida ("" = que decida el agente). */
export function getAgentConfig() {
    try {
        const raw = localStorage.getItem(LS_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            return { printer: parsed.printer || "", agentId: parsed.agentId || "", copies: parsed.copies || 1 };
        }
    } catch {
        /* ignore */
    }
    return { printer: "", agentId: "", copies: 1 };
}

export function setAgentConfig(patch) {
    const next = { ...getAgentConfig(), ...patch };
    try {
        localStorage.setItem(LS_KEY, JSON.stringify(next));
    } catch {
        /* modo privado: se ignora */
    }
    return next;
}

// ----- Agentes emparejados -----
export async function listAgents() {
    const { data } = await api.get("/agents");
    return data; // [{ id, name, online, lastSeenAt, printers, defaultPrinter, version }]
}

export async function createPairingCode() {
    const { data } = await api.post("/agents/pairing-code");
    return data; // { code: "ABCD-2345", expiresInSec: 600 }
}

/** Instaladores de la app del agente que hay en el servidor: { mac: {version,file,size,sha256}|null, windows: … }. */
export async function listInstallers() {
    const { data } = await api.get("/agent/installers");
    return data;
}

/** Enlace que abre la app instalada y le pasa el código para emparejarse (la app pide confirmación al usuario). */
export function pairingLink(server, code) {
    return `zebralab-agent://pair?server=${encodeURIComponent(server)}&code=${encodeURIComponent(code)}`;
}

export async function revokeAgent(id) {
    await api.delete(`/agents/${id}`);
}

/** Forma que esperan los modales (`agentInfo`) a partir de un agente en línea. */
export function agentInfoFrom(agent) {
    if (!agent) return null;
    return {
        ok: true,
        agentId: agent.id,
        name: agent.name,
        version: agent.version,
        default_printer: agent.defaultPrinter || "",
        printers: agent.printers || [],
    };
}

/**
 * Destinos de impresión posibles: por cada agente en línea, "Automática" (printer "": decide el agente,
 * que nunca usa la predeterminada del equipo) y una entrada por cada impresora que reporta.
 */
export function onlineTargets(agents = []) {
    const out = [];
    for (const a of agents.filter((x) => x.online)) {
        out.push({ agentId: a.id, agentName: a.name, platform: a.platform || "", printer: "", defaultPrinter: a.defaultPrinter || "" });
        for (const p of a.printers || []) {
            out.push({ agentId: a.id, agentName: a.name, platform: a.platform || "", printer: p, defaultPrinter: a.defaultPrinter || "" });
        }
    }
    return out;
}

/** El destino elegido en este navegador si sigue disponible; si no, "Automática" del agente más reciente. */
export function pickTarget(agents = []) {
    const targets = onlineTargets(agents);
    if (!targets.length) return { agentId: null, agentName: "", printer: "", defaultPrinter: "" };
    const cfg = getAgentConfig();
    // Por defecto: "Automática" de un equipo cuya impresora automática sí se puede resolver (reporta una
    // predeterminada); si ninguno la tiene, el primero. Con dos Zebras en un equipo, "Automática" no basta.
    const online = agents.filter((a) => a.online);
    const fallback = online.find((a) => a.defaultPrinter) || online[0];
    return (
        targets.find((t) => t.agentId === cfg.agentId && t.printer === cfg.printer) ||
        targets.find((t) => t.printer === "" && t.agentId === fallback.id)
    );
}

/** Info del agente en línea más reciente, o null si no hay ninguno conectado. */
export async function pingAgent() {
    try {
        const online = (await listAgents()).find((a) => a.online);
        return agentInfoFrom(online);
    } catch {
        return null;
    }
}

/**
 * Impresora a pedir en un trabajo: la elegida en este navegador SOLO si el agente la tiene;
 * si no (valor viejo, impresora renombrada), la que el agente propone, o "" para que él decida.
 */
export function pickPrinter(agentInfo) {
    const chosen = getAgentConfig().printer;
    if (chosen && (agentInfo?.printers || []).includes(chosen)) return chosen;
    return agentInfo?.default_printer || "";
}

// ----- Imprimir -----
/**
 * Deja el trabajo en la cola y espera a que el agente confirme que lo mandó a la impresora.
 *
 * `onStatus(estado)` avisa los cambios: queued → sent → done. Lanza Error con un mensaje listo
 * para mostrar si falla o no responde a tiempo. Si el trabajo sigue en cola al vencer el tiempo
 * se cancela, para que no salga una impresión sorpresa más tarde.
 */
export async function printZplDirect({ zpl, printer, agentId, copies = 1, title, labels, pages, onStatus, timeoutMs = 60000 }) {
    const body = { zpl: copies > 1 ? zpl.repeat(copies) : zpl };
    if (printer) body.printer = printer;
    if (agentId) body.agentId = agentId;
    if (title) body.title = String(title).slice(0, 120);
    if (labels) body.labels = labels;
    if (pages) body.pages = pages;

    let job;
    try {
        job = (await api.post("/print/jobs", body)).data;
    } catch (e) {
        throw new Error(e?.response?.data?.detail || "No se pudo enviar el trabajo al servidor");
    }
    let last = job.status;
    onStatus?.(last);

    const started = Date.now();
    while (Date.now() - started < timeoutMs) {
        await sleep(500);
        let current;
        try {
            current = (await api.get(`/print/jobs/${job.id}`)).data;
        } catch {
            continue; // red intermitente: se reintenta
        }
        if (current.status !== last) {
            last = current.status;
            onStatus?.(last);
        }
        if (current.status === "done") {
            window.dispatchEvent(new Event("zebralab:printed")); // el contador de la barra se actualiza solo
            return { ok: true, message: current.message, id: job.id };
        }
        if (FINAL_STATES.has(current.status)) {
            throw new Error(current.error || "El trabajo no se pudo imprimir");
        }
    }

    if (last === "queued") {
        await api.post(`/print/jobs/${job.id}/cancel`).catch(() => {});
        throw new Error("El agente no recogió el trabajo a tiempo; lo cancelé para que no salga más tarde.");
    }
    throw new Error(
        "El agente recibió el trabajo pero no confirmó el resultado. Revisa la impresora antes de repetirlo: podría haber salido."
    );
}
